import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Check, Clock3, Mail, RefreshCw, X } from "lucide-react";
import { Button } from "@/components/ui/button";

type ApplicationStatus = "pending" | "approved" | "rejected";
type EmailStatus = "not_sent" | "sent" | "failed" | "not_configured";

interface ApplicationHistoryEntry {
  id: number;
  eventType: "submission" | "decision";
  fullName: string;
  email: string;
  organization: string | null;
  role: string | null;
  intendedUse: string | null;
  status: ApplicationStatus;
  submittedAt: string;
  reviewedAt: string | null;
  createdAt: string;
}

interface EarlyAccessApplication {
  id: string;
  fullName: string;
  email: string;
  organization: string | null;
  role: string | null;
  intendedUse: string | null;
  status: ApplicationStatus;
  submittedAt: string;
  reviewedAt: string | null;
  submissionEmailStatus: EmailStatus;
  decisionEmailStatus: EmailStatus;
  history?: ApplicationHistoryEntry[];
}

interface QueueResponse {
  applications: EarlyAccessApplication[];
  emailConfigured: boolean;
}

interface ActionResponse {
  application: EarlyAccessApplication;
}

interface QueueAction {
  id: string;
  action: "approve" | "reject" | "resend-submission" | "resend-decision";
}

async function parseResponse<T>(response: Response): Promise<T> {
  const result = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(
      typeof result.error === "string"
        ? result.error
        : "The request could not be completed.",
    );
  }
  return result as T;
}

async function fetchApplications(): Promise<QueueResponse> {
  const response = await fetch("/api/admin/early-access/applications", {
    credentials: "include",
  });
  return parseResponse<QueueResponse>(response);
}

function emailStatusLabel(status: EmailStatus): string {
  switch (status) {
    case "sent":
      return "Sent";
    case "failed":
      return "Failed";
    case "not_configured":
      return "Not configured";
    default:
      return "Not sent";
  }
}

function statusBadgeClass(status: ApplicationStatus): string {
  if (status === "approved") {
    return "border-emerald-500/30 bg-emerald-500/10 text-emerald-300";
  }
  if (status === "rejected") {
    return "border-rose-500/30 bg-rose-500/10 text-rose-300";
  }
  return "border-amber-500/30 bg-amber-500/10 text-amber-200";
}

function formatDate(value: string): string {
  return new Date(value).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function ApplicationHistory({
  history,
}: {
  history: ApplicationHistoryEntry[] | undefined;
}) {
  if (!history?.length) return null;

  return (
    <details className="mt-4 border-t border-slate-800 pt-3">
      <summary className="cursor-pointer text-xs font-medium text-slate-300 hover:text-white">
        Application history ({history.length} events)
      </summary>
      <ol className="mt-3 space-y-3 border-l border-slate-700 pl-4">
        {[...history].reverse().map((entry) => {
          const eventDate =
            entry.eventType === "decision"
              ? entry.reviewedAt ?? entry.createdAt
              : entry.submittedAt;
          const title =
            entry.eventType === "submission"
              ? "Application submitted"
              : entry.status === "approved"
                ? "Application approved"
                : "Application rejected";

          return (
            <li key={entry.id} className="relative text-xs text-slate-400">
              <span className="absolute -left-[21px] top-1 h-2 w-2 rounded-full bg-slate-600" aria-hidden="true" />
              <p className="font-medium text-slate-200">{title}</p>
              <p className="mt-1">{formatDate(eventDate)}</p>
              {entry.eventType === "submission" && (
                <div className="mt-2 space-y-1">
                  <p className="text-slate-300">{entry.fullName}</p>
                  {(entry.role || entry.organization) && (
                    <p>{[entry.role, entry.organization].filter(Boolean).join(" · ")}</p>
                  )}
                  {entry.intendedUse && (
                    <p className="whitespace-pre-wrap leading-5">{entry.intendedUse}</p>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ol>
    </details>
  );
}

export function EarlyAccessQueue() {
  const queryClient = useQueryClient();
  const [notice, setNotice] = useState("");
  const [actionError, setActionError] = useState("");
  const applicationsQuery = useQuery({
    queryKey: ["/api/admin/early-access/applications"],
    queryFn: fetchApplications,
  });

  const actionMutation = useMutation({
    mutationFn: async ({ id, action }: QueueAction): Promise<ActionResponse> => {
      if (action === "approve" || action === "reject") {
        const response = await fetch(
          `/api/admin/early-access/applications/${encodeURIComponent(id)}/decision`,
          {
            method: "PATCH",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              decision: action === "approve" ? "approved" : "rejected",
            }),
          },
        );
        return parseResponse<ActionResponse>(response);
      }

      const response = await fetch(
        `/api/admin/early-access/applications/${encodeURIComponent(id)}/resend`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            type: action === "resend-submission" ? "submission" : "decision",
          }),
        },
      );
      return parseResponse<ActionResponse>(response);
    },
    onSuccess: ({ application }, variables) => {
      setActionError("");
      const didSend =
        variables.action === "approve" || variables.action === "reject"
          ? application.decisionEmailStatus === "sent"
          : variables.action === "resend-submission"
            ? application.submissionEmailStatus === "sent"
            : application.decisionEmailStatus === "sent";
      const message =
        variables.action === "approve"
          ? `Approved ${application.fullName}.`
          : variables.action === "reject"
            ? `Rejected ${application.fullName}.`
            : "Email retry completed.";
      setNotice(
        didSend
          ? `${message} The email was sent.`
          : `${message} Email status: ${emailStatusLabel(
              variables.action === "resend-submission"
                ? application.submissionEmailStatus
                : application.decisionEmailStatus,
            ).toLowerCase()}.`,
      );
      void queryClient.invalidateQueries({
        queryKey: ["/api/admin/early-access/applications"],
      });
    },
    onError: (error: Error) => {
      setActionError(error.message);
      setNotice("");
    },
  });

  const applications = applicationsQuery.data?.applications ?? [];
  const pending = applications.filter((item) => item.status === "pending");
  const reviewed = applications.filter((item) => item.status !== "pending");

  function review(application: EarlyAccessApplication, decision: "approve" | "reject") {
    const actionName = decision === "approve" ? "approve" : "reject";
    if (
      window.confirm(
        `${actionName === "approve" ? "Approve" : "Reject"} ${application.fullName}’s early access application?`,
      )
    ) {
      setNotice("");
      setActionError("");
      actionMutation.mutate({ id: application.id, action: decision });
    }
  }

  function retryEmail(
    application: EarlyAccessApplication,
    type: "submission" | "decision",
  ) {
    setNotice("");
    setActionError("");
    actionMutation.mutate({
      id: application.id,
      action: type === "submission" ? "resend-submission" : "resend-decision",
    });
  }

  return (
    <section className="mt-8 rounded-2xl border border-slate-800 bg-slate-900/70 p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-medium text-white">
            <Clock3 className="h-5 w-5 text-teal-300" aria-hidden="true" />
            Early access applications
          </h2>
          <p className="mt-2 text-sm text-slate-400">
            Review requests before applicants can open their private canvas.
          </p>
        </div>
        <div className="rounded-full border border-slate-700 px-3 py-1 text-sm text-slate-300">
          {pending.length} awaiting review
        </div>
      </div>

      {applicationsQuery.data && !applicationsQuery.data.emailConfigured && (
        <div className="mt-5 flex gap-3 rounded-xl border border-amber-500/25 bg-amber-500/5 p-4 text-sm leading-6 text-amber-100">
          <Mail className="mt-0.5 h-4 w-4 shrink-0 text-amber-300" aria-hidden="true" />
          <p>
            Resend isn’t fully configured yet. Applications and decisions will still be saved; configure the Resend key, verified sender address and public app URL to send emails. You can retry any email from this queue later.
          </p>
        </div>
      )}

      {notice && (
        <p role="status" className="mt-4 rounded-lg border border-emerald-500/20 bg-emerald-500/5 px-4 py-3 text-sm text-emerald-200">
          {notice}
        </p>
      )}
      {actionError && (
        <p role="alert" className="mt-4 rounded-lg border border-rose-500/20 bg-rose-500/5 px-4 py-3 text-sm text-rose-200">
          {actionError}
        </p>
      )}

      {applicationsQuery.isPending ? (
        <p className="mt-6 text-sm text-slate-400">Loading applications…</p>
      ) : applicationsQuery.isError ? (
        <div className="mt-6 rounded-xl border border-rose-500/20 bg-rose-500/5 p-4">
          <p className="text-sm text-rose-200">
            {applicationsQuery.error instanceof Error
              ? applicationsQuery.error.message
              : "Applications could not be loaded."}
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void applicationsQuery.refetch()}
            className="mt-3 border-slate-700 text-slate-200 hover:bg-slate-800"
          >
            Try again
          </Button>
        </div>
      ) : (
        <>
          <div className="mt-6 space-y-4">
            {pending.map((application) => (
              <article
                key={application.id}
                className="rounded-xl border border-slate-800 bg-slate-950/70 p-5"
              >
                <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-start">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-semibold text-white">{application.fullName}</h3>
                      <span className={`rounded-full border px-2.5 py-0.5 text-xs font-medium ${statusBadgeClass(application.status)}`}>
                        Pending
                      </span>
                    </div>
                    <a
                      href={`mailto:${application.email}`}
                      className="mt-1 inline-block break-all text-sm text-teal-300 hover:text-teal-200"
                    >
                      {application.email}
                    </a>
                    <p className="mt-2 text-xs text-slate-500">
                      Submitted {formatDate(application.submittedAt)}
                    </p>
                    {(application.role || application.organization) && (
                      <p className="mt-3 text-sm text-slate-300">
                        {[application.role, application.organization]
                          .filter(Boolean)
                          .join(" · ")}
                      </p>
                    )}
                    {application.intendedUse && (
                      <p className="mt-3 whitespace-pre-wrap text-sm leading-6 text-slate-400">
                        {application.intendedUse}
                      </p>
                    )}
                  </div>

                  <div className="flex shrink-0 flex-col gap-2 sm:min-w-36">
                    <Button
                      type="button"
                      size="sm"
                      disabled={actionMutation.isPending}
                      onClick={() => review(application, "approve")}
                      className="bg-emerald-700 text-white hover:bg-emerald-600"
                    >
                      <Check className="mr-2 h-4 w-4" aria-hidden="true" />
                      Approve
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={actionMutation.isPending}
                      onClick={() => review(application, "reject")}
                      className="border-rose-500/30 text-rose-200 hover:bg-rose-950/50"
                    >
                      <X className="mr-2 h-4 w-4" aria-hidden="true" />
                      Reject
                    </Button>
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-slate-800 pt-3 text-xs text-slate-400">
                  <span>Application email: {emailStatusLabel(application.submissionEmailStatus)}</span>
                  {application.submissionEmailStatus !== "sent" && (
                    <button
                      type="button"
                      disabled={actionMutation.isPending}
                      onClick={() => retryEmail(application, "submission")}
                      className="inline-flex items-center gap-1.5 font-medium text-teal-300 hover:text-teal-200 disabled:opacity-50"
                    >
                      <RefreshCw className="h-3 w-3" aria-hidden="true" />
                      Retry email
                    </button>
                  )}
                </div>
                <ApplicationHistory history={application.history} />
              </article>
            ))}
            {pending.length === 0 && (
              <div className="rounded-xl border border-dashed border-slate-800 px-5 py-8 text-center text-sm text-slate-500">
                No applications are waiting for review.
              </div>
            )}
          </div>

          {reviewed.length > 0 && (
            <details className="mt-6">
              <summary className="cursor-pointer text-sm font-medium text-slate-300 hover:text-white">
                Reviewed applications ({reviewed.length})
              </summary>
              <div className="mt-3 divide-y divide-slate-800 rounded-xl border border-slate-800">
                {reviewed.map((application) => (
                  <div
                    key={application.id}
                    className="flex flex-col justify-between gap-3 p-4 sm:flex-row sm:items-center"
                  >
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium text-white">{application.fullName}</span>
                        <span className={`rounded-full border px-2.5 py-0.5 text-xs font-medium ${statusBadgeClass(application.status)}`}>
                          {application.status === "approved" ? "Approved" : "Rejected"}
                        </span>
                      </div>
                      <a
                        href={`mailto:${application.email}`}
                        className="mt-1 inline-block break-all text-sm text-slate-400 hover:text-teal-300"
                      >
                        {application.email}
                      </a>
                      <p className="mt-2 text-xs text-slate-500">
                        Submitted {formatDate(application.submittedAt)}
                        {application.reviewedAt &&
                          ` · Reviewed ${formatDate(application.reviewedAt)}`}
                      </p>
                      <ApplicationHistory history={application.history} />
                    </div>
                    <div className="flex flex-wrap items-center gap-3 text-xs text-slate-400">
                      <span>Decision email: {emailStatusLabel(application.decisionEmailStatus)}</span>
                      {application.decisionEmailStatus !== "sent" && (
                        <button
                          type="button"
                          disabled={actionMutation.isPending}
                          onClick={() => retryEmail(application, "decision")}
                          className="inline-flex items-center gap-1.5 font-medium text-teal-300 hover:text-teal-200 disabled:opacity-50"
                        >
                          <RefreshCw className="h-3 w-3" aria-hidden="true" />
                          Retry
                        </button>
                      )}
                      {application.submissionEmailStatus !== "sent" && (
                        <button
                          type="button"
                          disabled={actionMutation.isPending}
                          onClick={() => retryEmail(application, "submission")}
                          className="inline-flex items-center gap-1.5 font-medium text-teal-300 hover:text-teal-200 disabled:opacity-50"
                        >
                          <RefreshCw className="h-3 w-3" aria-hidden="true" />
                          Retry application email
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </details>
          )}
        </>
      )}
    </section>
  );
}
